import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBan,
    faCalendarDays,
    faChevronDown,
    faChevronUp,
    faCheck,
    faClock,
    faPenToSquare,
    faMagnifyingGlass,
    faMoon,
    faPlus,
    faSun,
    faTrash,
    faXmark,
    faUserDoctor,
} from "@fortawesome/free-solid-svg-icons";
import { getFuncionarios } from "./FuncionarioService";
import {
    criarDiaBloqueadoAtendimentoMedico,
    criarDiaExtraAtendimentoMedico,
    criarConfiguracaoHorarioMedico,
    atualizarDiaExtraAtendimentoMedico,
    excluirDiaBloqueadoAtendimentoMedico,
    excluirDiaExtraAtendimentoMedico,
    getConfiguracoesAtendimentoMedicoPorUsuario,
} from "./ConfiguracaoHorarioMedicoService";
import {
    DIAS_DA_SEMANA_LABEL,
    formatarData,
    formatarHora,
    getDataAtendimentoEspecifico,
    getDataBloqueio,
    isConfiguracaoBloqueio,
    isConfiguracaoEspecifica,
    isConfiguracaoSemanal,
} from "./Helpers/AgendaValidation";

const TipoRegistro = {
    FIXO: "FIXO",
    ESPECIFICO: "ESPECIFICO",
    BLOQUEIO: "BLOQUEIO",
};

const normalizarTexto = (texto) =>
    String(texto ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const ordenarHorariosSemanais = (horarios) =>
    [...horarios].sort((a, b) => {
        const ordemA = DIAS_DA_SEMANA_LABEL[a.diasDaSemana]?.ordem ?? 99;
        const ordemB = DIAS_DA_SEMANA_LABEL[b.diasDaSemana]?.ordem ?? 99;

        if (ordemA !== ordemB) {
            return ordemA - ordemB;
        }

        return formatarHora(a.horaInicio).localeCompare(
            formatarHora(b.horaInicio),
        );
    });

const ordenarPorDataHora = (itens, getData) =>
    [...itens].sort((a, b) => {
        const dataComparacao = getData(a).localeCompare(getData(b));

        if (dataComparacao !== 0) {
            return dataComparacao;
        }

        return formatarHora(a.horaInicio).localeCompare(
            formatarHora(b.horaInicio),
        );
    });

const getNomeProfissional = (profissional) =>
    profissional.nomeSobrenome || profissional.username || profissional.label;

const getIniciais = (profissional) => {
    if (profissional.iniciaisDoNome) {
        return profissional.iniciaisDoNome.toUpperCase();
    }

    return getNomeProfissional(profissional)
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((parte) => parte[0])
        .join("")
        .toUpperCase();
};

const getPeriodo = (horaInicio, horaFim) => {
    const inicio = Number(formatarHora(horaInicio).split(":")[0]);
    const fim = Number(formatarHora(horaFim).split(":")[0]);

    if (inicio >= 18) {
        return { nome: "Noite", icon: faMoon, className: "text-indigo-500" };
    }

    if (inicio < 12 && fim <= 12) {
        return { nome: "Manhã", icon: faSun, className: "text-amber-500" };
    }

    if (inicio >= 12 && inicio < 18) {
        return { nome: "Tarde", icon: faSun, className: "text-orange-500" };
    }

    return { nome: "Integral", icon: faClock, className: "text-cyan-600" };
};

const criarNovoRegistro = (authResponseDTO) => ({
    tipo: TipoRegistro.FIXO,
    usuarioId:
        authResponseDTO?.funcao === "RECEPCIONISTA"
            ? ""
            : String(authResponseDTO?.id ?? ""),
    diasDaSemana: "",
    dataAtendimento: "",
    dataBloqueio: "",
    horaInicio: "",
    horaFim: "",
});

const separarConfiguracoes = (configuracoes) => {
    const lista = Array.isArray(configuracoes) ? configuracoes : [];

    return {
        configuracoesHorario: lista,
        horariosAtendimento: ordenarHorariosSemanais(
            lista.filter(isConfiguracaoSemanal),
        ),
        atendimentosEspecificos: ordenarPorDataHora(
            lista.filter(isConfiguracaoEspecifica),
            getDataAtendimentoEspecifico,
        ),
        diasBloqueados: ordenarPorDataHora(
            lista.filter(isConfiguracaoBloqueio),
            getDataBloqueio,
        ),
    };
};

const HorariosAtendimento = ({ authResponseDTO, token }) => {
    const [profissionais, setProfissionais] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState("");
    const [textoBusca, setTextoBusca] = useState("");
    const [profissionalAbertoId, setProfissionalAbertoId] = useState(null);
    const [exibeNovoRegistro, setExibeNovoRegistro] = useState(false);
    const [salvandoNovoRegistro, setSalvandoNovoRegistro] = useState(false);
    const [atendimentoEmEdicao, setAtendimentoEmEdicao] = useState(null);
    const [atendimentoProcessandoId, setAtendimentoProcessandoId] =
        useState(null);
    const [bloqueioProcessandoId, setBloqueioProcessandoId] = useState(null);
    const [novoRegistro, setNovoRegistro] = useState(() =>
        criarNovoRegistro(authResponseDTO),
    );

    const carregarConfiguracoesDoProfissional = useCallback(async (profissional) => {
        const configuracoes = await getConfiguracoesAtendimentoMedicoPorUsuario(
            token,
            profissional.id,
        );

        return {
            ...profissional,
            ...separarConfiguracoes(configuracoes),
            erroHorarios: false,
        };
    }, [token]);

    const carregarProfissionais = useCallback(async () => {
        setCarregando(true);
        setErro("");

        try {
            let funcionarios = [];

            try {
                funcionarios = await getFuncionarios(token);
            } catch (error) {
                if (authResponseDTO?.id) {
                    funcionarios = [authResponseDTO];
                } else {
                    throw error;
                }
            }

            const profissionaisAtendimento = Array.isArray(funcionarios)
                ? funcionarios
                : [];

            const profissionaisComHorarios = await Promise.all(
                profissionaisAtendimento.map(async (profissional) => {
                    try {
                        return await carregarConfiguracoesDoProfissional(
                            profissional,
                        );
                    } catch (error) {
                        console.error(
                            "Erro ao buscar horários do profissional:",
                            error,
                        );

                        return {
                            ...profissional,
                            ...separarConfiguracoes([]),
                            erroHorarios: true,
                        };
                    }
                }),
            );

            setProfissionais(profissionaisComHorarios);
            setProfissionalAbertoId(profissionaisComHorarios[0]?.id ?? null);
        } catch (error) {
            console.error("Erro ao buscar profissionais:", error);
            setProfissionais([]);
            setErro("Não foi possível carregar os profissionais.");
        } finally {
            setCarregando(false);
        }
    }, [authResponseDTO, carregarConfiguracoesDoProfissional, token]);

    useEffect(() => {
        let ativo = true;

        const carregar = async () => {
            if (!ativo) {
                return;
            }

            await carregarProfissionais();
        };

        carregar();

        return () => {
            ativo = false;
        };
    }, [carregarProfissionais]);

    const profissionaisFiltrados = useMemo(() => {
        const buscaNormalizada = normalizarTexto(textoBusca);

        return profissionais.filter((profissional) => {
            const nome = getNomeProfissional(profissional);
            const especialidade =
                profissional.especialidade || profissional.funcao || "";
            return (
                !buscaNormalizada ||
                normalizarTexto(`${nome} ${especialidade}`).includes(
                    buscaNormalizada,
                )
            );
        });
    }, [profissionais, textoBusca]);

    const profissionaisParaSelecao = useMemo(
        () =>
            profissionais.filter(
                (profissional) => profissional.funcao !== "RECEPCIONISTA",
            ),
        [profissionais],
    );

    const alternarProfissional = (profissionalId) => {
        setProfissionalAbertoId((idAtual) =>
            idAtual === profissionalId ? null : profissionalId,
        );
    };

    const alterarNovoRegistro = (event) => {
        const { name, value } = event.target;

        setNovoRegistro((registroAtual) => ({
            ...registroAtual,
            [name]: value,
        }));
    };

    const alterarTipoRegistro = (tipo) => {
        setNovoRegistro((registroAtual) => ({
            ...criarNovoRegistro(authResponseDTO),
            usuarioId: registroAtual.usuarioId,
            tipo,
        }));
    };

    const fecharFormularioNovoRegistro = () => {
        setExibeNovoRegistro(false);
        setNovoRegistro(criarNovoRegistro(authResponseDTO));
    };

    const atualizarProfissionalAposSalvar = async (usuarioId) => {
        const profissionalAtual = profissionais.find(
            (item) => item.id === usuarioId,
        );

        if (!profissionalAtual) {
            return;
        }

        const profissionalAtualizado = await carregarConfiguracoesDoProfissional(
            profissionalAtual,
        );

        setProfissionais((profissionaisAtuais) =>
            profissionaisAtuais.map((item) =>
                item.id === usuarioId ? profissionalAtualizado : item,
            ),
        );
    };

    const validarNovoRegistro = () => {
        if (!novoRegistro.usuarioId) {
            alert("Selecione o profissional.");
            return false;
        }

        if (
            novoRegistro.tipo !== TipoRegistro.BLOQUEIO &&
            (!novoRegistro.horaInicio || !novoRegistro.horaFim)
        ) {
            alert("Informe o horário de início e fim.");
            return false;
        }

        if (
            novoRegistro.tipo !== TipoRegistro.BLOQUEIO &&
            novoRegistro.horaFim <= novoRegistro.horaInicio
        ) {
            alert("O horário final deve ser maior que o horário inicial.");
            return false;
        }

        const usuarioId = Number(novoRegistro.usuarioId);
        const profissional = profissionais.find((item) => item.id === usuarioId);

        if (novoRegistro.tipo === TipoRegistro.FIXO) {
            if (!novoRegistro.diasDaSemana) {
                alert("Selecione o dia da semana.");
                return false;
            }

            const jaPossuiDia = profissional?.horariosAtendimento?.some(
                (horario) =>
                    horario.diasDaSemana === novoRegistro.diasDaSemana,
            );

            if (jaPossuiDia) {
                alert(
                    "Este profissional já possui horário fixo cadastrado para este dia.",
                );
                return false;
            }
        }

        if (novoRegistro.tipo === TipoRegistro.ESPECIFICO) {
            if (!novoRegistro.dataAtendimento) {
                alert("Informe a data de atendimento.");
                return false;
            }

            const jaPossuiData = profissional?.atendimentosEspecificos?.some(
                (atendimento) =>
                    getDataAtendimentoEspecifico(atendimento) ===
                    novoRegistro.dataAtendimento,
            );

            if (jaPossuiData) {
                alert(
                    "Este profissional já possui atendimento específico cadastrado para esta data.",
                );
                return false;
            }
        }

        if (novoRegistro.tipo === TipoRegistro.BLOQUEIO) {
            if (!novoRegistro.dataBloqueio) {
                alert("Informe a data de bloqueio.");
                return false;
            }

            // const usuarioLogadoId = Number(authResponseDTO?.id);

            // if (usuarioLogadoId !== usuarioId) {
            //     alert(
            //         "Somente o próprio profissional pode bloquear dias de atendimento.",
            //     );
            //     return false;
            // }

            const jaPossuiBloqueio = profissional?.diasBloqueados?.some(
                (bloqueio) =>
                    getDataBloqueio(bloqueio) === novoRegistro.dataBloqueio,
            );

            if (jaPossuiBloqueio) {
                alert("Esta data já está bloqueada para este profissional.");
                return false;
            }
        }

        return true;
    };

    const montarPayloadNovoRegistro = () => {
        const usuarioId = Number(novoRegistro.usuarioId);

        if (novoRegistro.tipo === TipoRegistro.FIXO) {
            return {
                usuarioId,
                diasDaSemana: novoRegistro.diasDaSemana,
                horaInicio: novoRegistro.horaInicio,
                horaFim: novoRegistro.horaFim,
            };
        }

        if (novoRegistro.tipo === TipoRegistro.ESPECIFICO) {
            return {
                usuarioId,
                dataAtendimento: novoRegistro.dataAtendimento,
                horaInicio: novoRegistro.horaInicio,
                horaFim: novoRegistro.horaFim,
            };
        }

        return {
            usuarioId,
            dataBloqueio: novoRegistro.dataBloqueio,
        };
    };

    const salvarRegistroPorTipo = () => {
        const payload = montarPayloadNovoRegistro();

        if (novoRegistro.tipo === TipoRegistro.FIXO) {
            return criarConfiguracaoHorarioMedico(token, payload);
        }

        if (novoRegistro.tipo === TipoRegistro.ESPECIFICO) {
            return criarDiaExtraAtendimentoMedico(token, payload);
        }

        return criarDiaBloqueadoAtendimentoMedico(token, payload);
    };

    const salvarNovoRegistro = async (event) => {
        event.preventDefault();

        if (!validarNovoRegistro()) {
            return;
        }

        const usuarioId = Number(novoRegistro.usuarioId);

        setSalvandoNovoRegistro(true);

        try {
            await salvarRegistroPorTipo();

            await atualizarProfissionalAposSalvar(usuarioId);
            setProfissionalAbertoId(usuarioId);
            fecharFormularioNovoRegistro();
            alert("Configuração de atendimento salva com sucesso.");
        } catch (error) {
            console.error("Erro ao salvar configuração de atendimento:", error);
            alert(
                error?.message ||
                    "Não foi possível salvar a configuração de atendimento.",
            );
        } finally {
            setSalvandoNovoRegistro(false);
        }
    };

    const iniciarEdicaoAtendimento = (profissionalId, atendimento) => {
        if (!atendimento.id) {
            alert("Não foi possível identificar o atendimento selecionado.");
            return;
        }

        setAtendimentoEmEdicao({
            id: atendimento.id,
            usuarioId: Number(profissionalId),
            dataAtendimento: getDataAtendimentoEspecifico(atendimento),
            horaInicio: formatarHora(atendimento.horaInicio),
            horaFim: formatarHora(atendimento.horaFim),
        });
    };

    const cancelarEdicaoAtendimento = () => {
        setAtendimentoEmEdicao(null);
    };

    const alterarAtendimentoEmEdicao = (campo, valor) => {
        setAtendimentoEmEdicao((atendimentoAtual) =>
            atendimentoAtual
                ? { ...atendimentoAtual, [campo]: valor }
                : atendimentoAtual,
        );
    };

    const validarAtendimentoEmEdicao = () => {
        if (!atendimentoEmEdicao?.dataAtendimento) {
            alert("Informe a data de atendimento.");
            return false;
        }

        if (
            !atendimentoEmEdicao.horaInicio ||
            !atendimentoEmEdicao.horaFim
        ) {
            alert("Informe o horário de início e fim.");
            return false;
        }

        if (atendimentoEmEdicao.horaFim <= atendimentoEmEdicao.horaInicio) {
            alert("O horário final deve ser maior que o horário inicial.");
            return false;
        }

        const profissional = profissionais.find(
            (item) => item.id === atendimentoEmEdicao.usuarioId,
        );
        const jaPossuiData = profissional?.atendimentosEspecificos?.some(
            (atendimento) =>
                atendimento.id !== atendimentoEmEdicao.id &&
                getDataAtendimentoEspecifico(atendimento) ===
                    atendimentoEmEdicao.dataAtendimento,
        );

        if (jaPossuiData) {
            alert(
                "Este profissional já possui atendimento específico cadastrado para esta data.",
            );
            return false;
        }

        return true;
    };

    const salvarEdicaoAtendimento = async () => {
        if (!validarAtendimentoEmEdicao()) {
            return;
        }

        const payload = {
            usuarioId: atendimentoEmEdicao.usuarioId,
            dataAtendimento: atendimentoEmEdicao.dataAtendimento,
            horaInicio: atendimentoEmEdicao.horaInicio,
            horaFim: atendimentoEmEdicao.horaFim,
        };

        setAtendimentoProcessandoId(atendimentoEmEdicao.id);

        try {
            await atualizarDiaExtraAtendimentoMedico(
                token,
                atendimentoEmEdicao.id,
                payload,
            );
            await atualizarProfissionalAposSalvar(
                atendimentoEmEdicao.usuarioId,
            );
            setAtendimentoEmEdicao(null);
            alert("Atendimento específico atualizado com sucesso.");
        } catch (error) {
            console.error("Erro ao atualizar atendimento específico:", error);
            alert(
                error?.message ||
                    "Não foi possível atualizar o atendimento específico.",
            );
        } finally {
            setAtendimentoProcessandoId(null);
        }
    };

    const excluirAtendimento = async (profissionalId, atendimento) => {
        if (!atendimento.id) {
            alert("Não foi possível identificar o atendimento selecionado.");
            return;
        }

        const confirmarExclusao = window.confirm(
            "Deseja excluir este atendimento específico?",
        );

        if (!confirmarExclusao) {
            return;
        }

        setAtendimentoProcessandoId(atendimento.id);

        try {
            await excluirDiaExtraAtendimentoMedico(token, atendimento.id);
            await atualizarProfissionalAposSalvar(Number(profissionalId));

            if (atendimentoEmEdicao?.id === atendimento.id) {
                setAtendimentoEmEdicao(null);
            }

            alert("Atendimento específico excluído com sucesso.");
        } catch (error) {
            console.error("Erro ao excluir atendimento específico:", error);
            alert(
                error?.message ||
                    "Não foi possível excluir o atendimento específico.",
            );
        } finally {
            setAtendimentoProcessandoId(null);
        }
    };

    const excluirBloqueio = async (profissionalId, bloqueio) => {
        if (!bloqueio.id) {
            alert("Não foi possível identificar o dia bloqueado selecionado.");
            return;
        }

        const confirmarExclusao = window.confirm(
            "Deseja excluir este dia bloqueado?",
        );

        if (!confirmarExclusao) {
            return;
        }

        setBloqueioProcessandoId(bloqueio.id);

        try {
            await excluirDiaBloqueadoAtendimentoMedico(token, bloqueio.id);
            await atualizarProfissionalAposSalvar(Number(profissionalId));
            alert("Dia bloqueado excluído com sucesso.");
        } catch (error) {
            console.error("Erro ao excluir dia bloqueado:", error);
            alert(error?.message || "Não foi possível excluir o dia bloqueado.");
        } finally {
            setBloqueioProcessandoId(null);
        }
    };

    const exibirCampoHorario = novoRegistro.tipo !== TipoRegistro.BLOQUEIO;

    return (
        <section className="bg-gray-50 min-h-screen p-3 sm:p-5">
            <div className="mx-auto max-w-screen-2xl px-2 lg:px-8">
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-4 sm:p-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-slate-800 text-white shadow-sm">
                                <FontAwesomeIcon
                                    icon={faCalendarDays}
                                    className="text-xl"
                                />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                                    Horários de Atendimento
                                </h1>
                                <p className="mt-1 text-sm sm:text-base text-slate-600">
                                    Consulte horários fixos, atendimentos específicos e dias bloqueados.
                                </p>
                            </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] lg:w-[640px]">
                            <div className="relative">
                                <input
                                    value={textoBusca}
                                    onChange={(event) =>
                                        setTextoBusca(event.target.value)
                                    }
                                    type="search"
                                    placeholder="Buscar profissional..."
                                    className="h-12 w-full rounded-lg border border-gray-300 bg-white pl-4 pr-11 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                />
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setNovoRegistro(
                                        criarNovoRegistro(authResponseDTO),
                                    );
                                    setExibeNovoRegistro((exibe) => !exibe);
                                }}
                                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-200"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Novo registro
                            </button>
                        </div>
                    </div>

                    {exibeNovoRegistro && (
                        <form
                            onSubmit={salvarNovoRegistro}
                            className="mt-6 rounded-lg border border-cyan-100 bg-cyan-50 p-4"
                        >
                            <div className="mb-4 flex flex-wrap gap-2">
                                {Object.values(TipoRegistro).map((tipo) => (
                                    <button
                                        key={tipo}
                                        type="button"
                                        onClick={() => alterarTipoRegistro(tipo)}
                                        className={`h-10 rounded-lg px-4 text-sm font-semibold ${
                                            novoRegistro.tipo === tipo
                                                ? "bg-cyan-700 text-white"
                                                : "bg-white text-slate-700 border border-gray-300"
                                        }`}
                                    >
                                        {tipo === TipoRegistro.FIXO &&
                                            "Horário fixo"}
                                        {tipo === TipoRegistro.ESPECIFICO &&
                                            "Dia específico"}
                                        {tipo === TipoRegistro.BLOQUEIO &&
                                            "Bloquear dia"}
                                    </button>
                                ))}
                            </div>

                            <div className="grid gap-4 md:grid-cols-5">
                                <div>
                                    <label
                                        htmlFor="novoRegistroProfissional"
                                        className="mb-2 block text-sm font-semibold text-slate-800"
                                    >
                                        Profissional
                                    </label>
                                    <select
                                        id="novoRegistroProfissional"
                                        name="usuarioId"
                                        value={novoRegistro.usuarioId}
                                        onChange={alterarNovoRegistro}
                                        className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                        required
                                    >
                                        <option value="">Selecione</option>
                                        {profissionaisParaSelecao.map(
                                            (profissional) => (
                                                <option
                                                    key={profissional.id}
                                                    value={profissional.id}
                                                >
                                                    {getNomeProfissional(
                                                        profissional,
                                                    )}
                                                </option>
                                            ),
                                        )}
                                    </select>
                                </div>

                                {novoRegistro.tipo === TipoRegistro.FIXO && (
                                    <div>
                                        <label
                                            htmlFor="novoRegistroDiaSemana"
                                            className="mb-2 block text-sm font-semibold text-slate-800"
                                        >
                                            Dia da semana
                                        </label>
                                        <select
                                            id="novoRegistroDiaSemana"
                                            name="diasDaSemana"
                                            value={novoRegistro.diasDaSemana}
                                            onChange={alterarNovoRegistro}
                                            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                            required
                                        >
                                            <option value="">Selecione</option>
                                            {Object.entries(
                                                DIAS_DA_SEMANA_LABEL,
                                            ).map(([valor, dia]) => (
                                                <option
                                                    key={valor}
                                                    value={valor}
                                                >
                                                    {dia.nome}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {novoRegistro.tipo ===
                                    TipoRegistro.ESPECIFICO && (
                                    <div>
                                        <label
                                            htmlFor="novoRegistroDataAtendimento"
                                            className="mb-2 block text-sm font-semibold text-slate-800"
                                        >
                                            Data
                                        </label>
                                        <input
                                            id="novoRegistroDataAtendimento"
                                            name="dataAtendimento"
                                            type="date"
                                            value={
                                                novoRegistro.dataAtendimento
                                            }
                                            onChange={alterarNovoRegistro}
                                            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                            required
                                        />
                                    </div>
                                )}

                                {novoRegistro.tipo === TipoRegistro.BLOQUEIO && (
                                    <div>
                                        <label
                                            htmlFor="novoRegistroDataBloqueio"
                                            className="mb-2 block text-sm font-semibold text-slate-800"
                                        >
                                            Data bloqueada
                                        </label>
                                        <input
                                            id="novoRegistroDataBloqueio"
                                            name="dataBloqueio"
                                            type="date"
                                            value={novoRegistro.dataBloqueio}
                                            onChange={alterarNovoRegistro}
                                            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                            required
                                        />
                                    </div>
                                )}

                                {exibirCampoHorario && (
                                    <>
                                        <div>
                                            <label
                                                htmlFor="novoRegistroInicio"
                                                className="mb-2 block text-sm font-semibold text-slate-800"
                                            >
                                                Início
                                            </label>
                                            <input
                                                id="novoRegistroInicio"
                                                name="horaInicio"
                                                type="time"
                                                value={novoRegistro.horaInicio}
                                                onChange={alterarNovoRegistro}
                                                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="novoRegistroFim"
                                                className="mb-2 block text-sm font-semibold text-slate-800"
                                            >
                                                Fim
                                            </label>
                                            <input
                                                id="novoRegistroFim"
                                                name="horaFim"
                                                type="time"
                                                value={novoRegistro.horaFim}
                                                onChange={alterarNovoRegistro}
                                                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                                required
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            {novoRegistro.tipo === TipoRegistro.BLOQUEIO && (
                                <p className="mt-3 text-sm text-slate-600">
                                    O bloqueio será confirmado apenas se não houver agendamento marcado para a data.
                                </p>
                            )}

                            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                                <button
                                    type="button"
                                    onClick={fecharFormularioNovoRegistro}
                                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-gray-50"
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={salvandoNovoRegistro}
                                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    {salvandoNovoRegistro
                                        ? "Salvando..."
                                        : "Salvar"}
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="mt-8 space-y-3">
                        {carregando && (
                            <div className="rounded-lg border border-gray-200 p-6 text-center text-sm text-slate-600">
                                Carregando horários de atendimento...
                            </div>
                        )}

                        {!carregando && erro && (
                            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                {erro}
                            </div>
                        )}

                        {!carregando &&
                            !erro &&
                            profissionaisFiltrados.length === 0 && (
                                <div className="rounded-lg border border-gray-200 p-6 text-center text-sm text-slate-600">
                                    Nenhum profissional encontrado.
                                </div>
                            )}

                        {!carregando &&
                            profissionaisFiltrados.map((profissional) => {
                                const aberto =
                                    profissionalAbertoId === profissional.id;
                                const horarios =
                                    profissional.horariosAtendimento ?? [];
                                const atendimentosEspecificos =
                                    profissional.atendimentosEspecificos ?? [];
                                const diasBloqueados =
                                    profissional.diasBloqueados ?? [];
                                const semRegistros =
                                    horarios.length === 0 &&
                                    atendimentosEspecificos.length === 0 &&
                                    diasBloqueados.length === 0;

                                return (
                                    <div
                                        key={profissional.id}
                                        className="rounded-lg border border-gray-200 bg-white shadow-sm"
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                alternarProfissional(
                                                    profissional.id,
                                                )
                                            }
                                            className="flex w-full items-center gap-4 p-4 text-left"
                                        >
                                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-sm font-bold text-cyan-800">
                                                {getIniciais(profissional) || (
                                                    <FontAwesomeIcon
                                                        icon={faUserDoctor}
                                                    />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h2 className="truncate text-lg font-bold text-blue-700">
                                                    {getNomeProfissional(
                                                        profissional,
                                                    )}
                                                </h2>
                                                <p className="truncate text-sm text-slate-600">
                                                    {profissional.especialidade ||
                                                        profissional.funcao ||
                                                        "Especialidade não informada"}
                                                </p>
                                            </div>
                                            <FontAwesomeIcon
                                                icon={
                                                    aberto
                                                        ? faChevronUp
                                                        : faChevronDown
                                                }
                                                className="text-slate-900"
                                            />
                                        </button>

                                        {aberto && (
                                            <div className="space-y-4 px-4 pb-4 sm:px-6">
                                                {profissional.erroHorarios && (
                                                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                                                        Não foi possível carregar os horários deste profissional.
                                                    </p>
                                                )}

                                                {semRegistros && (
                                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-slate-600">
                                                        Configurações de atendimento não informadas.
                                                    </div>
                                                )}

                                                {horarios.length > 0 && (
                                                    <TabelaHorariosSemanais
                                                        horarios={horarios}
                                                    />
                                                )}

                                                {atendimentosEspecificos.length >
                                                    0 && (
                                                    <TabelaAtendimentosEspecificos
                                                        profissionalId={
                                                            profissional.id
                                                        }
                                                        atendimentos={
                                                            atendimentosEspecificos
                                                        }
                                                        atendimentoEmEdicao={
                                                            atendimentoEmEdicao
                                                        }
                                                        atendimentoProcessandoId={
                                                            atendimentoProcessandoId
                                                        }
                                                        onEditar={
                                                            iniciarEdicaoAtendimento
                                                        }
                                                        onCancelarEdicao={
                                                            cancelarEdicaoAtendimento
                                                        }
                                                        onAlterarEdicao={
                                                            alterarAtendimentoEmEdicao
                                                        }
                                                        onSalvarEdicao={
                                                            salvarEdicaoAtendimento
                                                        }
                                                        onExcluir={
                                                            excluirAtendimento
                                                        }
                                                    />
                                                )}

                                                {diasBloqueados.length > 0 && (
                                                    <ListaDiasBloqueados
                                                        profissionalId={
                                                            profissional.id
                                                        }
                                                        diasBloqueados={
                                                            diasBloqueados
                                                        }
                                                        bloqueioProcessandoId={
                                                            bloqueioProcessandoId
                                                        }
                                                        onExcluir={
                                                            excluirBloqueio
                                                        }
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
        </section>
    );
};

const TituloSecao = ({ icon, children }) => (
    <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <FontAwesomeIcon icon={icon} className="text-cyan-700" />
        {children}
    </div>
);

const TabelaHorariosSemanais = ({ horarios }) => (
    <div>
        <TituloSecao icon={faCalendarDays}>Horários fixos</TituloSecao>
        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-white">
                    <tr>
                        <th className="whitespace-nowrap px-6 py-4 text-left font-bold text-slate-900">
                            Dia da semana
                        </th>
                        <th className="whitespace-nowrap px-6 py-4 text-left font-bold text-slate-900">
                            Período
                        </th>
                        <th className="whitespace-nowrap px-6 py-4 text-left font-bold text-slate-900">
                            Horário
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {horarios.map((horario) => {
                        const periodo = getPeriodo(
                            horario.horaInicio,
                            horario.horaFim,
                        );

                        return (
                            <tr
                                key={
                                    horario.id ??
                                    `${horario.diasDaSemana}-${horario.horaInicio}-${horario.horaFim}`
                                }
                            >
                                <td className="whitespace-nowrap px-6 py-4 font-medium text-slate-900">
                                    {DIAS_DA_SEMANA_LABEL[
                                        horario.diasDaSemana
                                    ]?.nome || horario.diasDaSemana}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                                    <span className="inline-flex items-center gap-3">
                                        <FontAwesomeIcon
                                            icon={periodo.icon}
                                            className={`text-lg ${periodo.className}`}
                                        />
                                        {periodo.nome}
                                    </span>
                                </td>
                                <td className="whitespace-nowrap px-6 py-4">
                                    <span className="inline-flex items-center gap-2 rounded-md bg-green-100 px-3 py-1 text-green-700">
                                        {formatarHora(horario.horaInicio)} -{" "}
                                        {formatarHora(horario.horaFim)}
                                        <FontAwesomeIcon icon={faClock} />
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

const TabelaAtendimentosEspecificos = ({
    profissionalId,
    atendimentos,
    atendimentoEmEdicao,
    atendimentoProcessandoId,
    onEditar,
    onCancelarEdicao,
    onAlterarEdicao,
    onSalvarEdicao,
    onExcluir,
}) => (
    <div>
        <TituloSecao icon={faPlus}>Dias específicos</TituloSecao>
        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-white">
                    <tr>
                        <th className="whitespace-nowrap px-6 py-4 text-left font-bold text-slate-900">
                            Data
                        </th>
                        <th className="whitespace-nowrap px-6 py-4 text-left font-bold text-slate-900">
                            Horário
                        </th>
                        <th className="whitespace-nowrap px-6 py-4 text-right font-bold text-slate-900">
                            Ações
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {atendimentos.map((atendimento) => {
                        const editando =
                            atendimentoEmEdicao?.id === atendimento.id;
                        const processando =
                            atendimentoProcessandoId === atendimento.id;

                        return (
                            <tr
                                key={
                                    atendimento.id ??
                                    `${getDataAtendimentoEspecifico(
                                        atendimento,
                                    )}-${atendimento.horaInicio}-${atendimento.horaFim}`
                                }
                            >
                                <td className="whitespace-nowrap px-6 py-4 font-medium text-slate-900">
                                    {editando ? (
                                        <input
                                            type="date"
                                            value={
                                                atendimentoEmEdicao.dataAtendimento
                                            }
                                            onChange={(event) =>
                                                onAlterarEdicao(
                                                    "dataAtendimento",
                                                    event.target.value,
                                                )
                                            }
                                            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                            required
                                        />
                                    ) : (
                                        formatarData(
                                            getDataAtendimentoEspecifico(
                                                atendimento,
                                            ),
                                        )
                                    )}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4">
                                    {editando ? (
                                        <div className="flex min-w-[220px] items-center gap-2">
                                            <input
                                                type="time"
                                                value={
                                                    atendimentoEmEdicao.horaInicio
                                                }
                                                onChange={(event) =>
                                                    onAlterarEdicao(
                                                        "horaInicio",
                                                        event.target.value,
                                                    )
                                                }
                                                className="h-10 w-28 rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                                required
                                            />
                                            <input
                                                type="time"
                                                value={
                                                    atendimentoEmEdicao.horaFim
                                                }
                                                onChange={(event) =>
                                                    onAlterarEdicao(
                                                        "horaFim",
                                                        event.target.value,
                                                    )
                                                }
                                                className="h-10 w-28 rounded-lg border border-gray-300 bg-white px-3 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                                                required
                                            />
                                        </div>
                                    ) : (
                                        <span className="inline-flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1 text-blue-700">
                                            {formatarHora(
                                                atendimento.horaInicio,
                                            )}{" "}
                                            - {formatarHora(atendimento.horaFim)}
                                            <FontAwesomeIcon icon={faClock} />
                                        </span>
                                    )}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4">
                                    <div className="flex justify-end gap-2">
                                        {editando ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={onSalvarEdicao}
                                                    disabled={processando}
                                                    title="Salvar atendimento específico"
                                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-70"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                    />
                                                    <span className="sr-only">
                                                        Salvar
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={onCancelarEdicao}
                                                    disabled={processando}
                                                    title="Cancelar edição"
                                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-slate-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faXmark}
                                                    />
                                                    <span className="sr-only">
                                                        Cancelar
                                                    </span>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onEditar(
                                                            profissionalId,
                                                            atendimento,
                                                        )
                                                    }
                                                    disabled={
                                                        Boolean(
                                                            atendimentoProcessandoId,
                                                        ) || !atendimento.id
                                                    }
                                                    title="Editar atendimento específico"
                                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-70"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPenToSquare}
                                                    />
                                                    <span className="sr-only">
                                                        Editar
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onExcluir(
                                                            profissionalId,
                                                            atendimento,
                                                        )
                                                    }
                                                    disabled={
                                                        Boolean(
                                                            atendimentoProcessandoId,
                                                        ) || !atendimento.id
                                                    }
                                                    title="Excluir atendimento específico"
                                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                    />
                                                    <span className="sr-only">
                                                        Excluir
                                                    </span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

const ListaDiasBloqueados = ({
    profissionalId,
    diasBloqueados,
    bloqueioProcessandoId,
    onExcluir,
}) => (
    <div>
        <TituloSecao icon={faBan}>Dias bloqueados</TituloSecao>
        <div className="mt-2 flex flex-wrap gap-2">
            {diasBloqueados.map((bloqueio) => {
                const processando = bloqueioProcessandoId === bloqueio.id;

                return (
                    <span
                        key={bloqueio.id ?? getDataBloqueio(bloqueio)}
                        className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                    >
                        <FontAwesomeIcon icon={faBan} />
                        {formatarData(getDataBloqueio(bloqueio))}
                        <button
                            type="button"
                            onClick={() => onExcluir(profissionalId, bloqueio)}
                            disabled={processando || !bloqueio.id}
                            title="Excluir dia bloqueado"
                            className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-red-700 hover:bg-red-100 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <FontAwesomeIcon
                                icon={faXmark}
                                className="text-xs"
                            />
                            <span className="sr-only">
                                Excluir dia bloqueado
                            </span>
                        </button>
                    </span>
                );
            })}
        </div>
    </div>
);

export default HorariosAtendimento;
